import dayjs from "dayjs";
import { createSlice, PayloadAction, createEntityAdapter } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { ChatRoom, ChatRoomMessage, ChatRoomMessageSync } from "./types";
import {
  getParentChatRooms,
  getParentChatRoomMessages,
  markParentChatRoomMessage,
} from "./api";

// Entity Adapters
export const parentChatRoomAdapter = createEntityAdapter<ChatRoom>({
  sortComparer: (a, b) => (dayjs(a.created_at) < dayjs(b.created_at) ? -1 : 1),
});

export const parentChatRoomMessageAdapter = createEntityAdapter<ChatRoomMessage>({
  sortComparer: (a, b) => (dayjs(a.created_at) < dayjs(b.created_at) ? -1 : 1),
});

export const parentChatRoomMessageSyncAdapter = createEntityAdapter<ChatRoomMessageSync>();

// Selectors
export const { selectAll: selectAllParentChatRooms } = parentChatRoomAdapter.getSelectors(
  (state: RootState) => state.message.chatRooms,
);

export const { selectAll: selectAllParentChatRoomMessages } =
  parentChatRoomMessageAdapter.getSelectors((state: RootState) => state.message.chatRoomMessages);

export const {
  selectAll: selectAllParentChatRoomMessageSync,
  selectById: selectParentChatRoomMessageSyncById,
} = parentChatRoomMessageSyncAdapter.getSelectors(
  (state: RootState) => state.message.chatRoomMessageSync,
);

// State
export interface MessageState {
  selectedChatRoomId: string | null;
  isRefreshed: boolean;
  chatRooms: ReturnType<typeof parentChatRoomAdapter.getInitialState>;
  chatRoomMessages: ReturnType<typeof parentChatRoomMessageAdapter.getInitialState>;
  chatRoomMessageSync: ReturnType<typeof parentChatRoomMessageSyncAdapter.getInitialState>;
}

const initialState: MessageState = {
  selectedChatRoomId: null,
  isRefreshed: false,
  chatRooms: parentChatRoomAdapter.getInitialState(),
  chatRoomMessages: parentChatRoomMessageAdapter.getInitialState(),
  chatRoomMessageSync: parentChatRoomMessageSyncAdapter.getInitialState(),
};

// Slice
const messageSlice = createSlice({
  name: "message",
  initialState,
  reducers: {
    setSelectedChatRoomId(state: MessageState, action: PayloadAction<string | null>) {
      state.selectedChatRoomId = action.payload;
      if (action.payload) {
        const sync = parentChatRoomMessageSyncAdapter
          .getSelectors()
          .selectById(state.chatRoomMessageSync, action.payload);
        if (sync?.latestMessageCreatedAt) {
          parentChatRoomMessageSyncAdapter.updateOne(state.chatRoomMessageSync, {
            id: action.payload,
            changes: { latestReadAt: dayjs().format("YYYY-MM-DDTHH:mm:ssZ") },
          });
        }
      }
    },
    setIsRefreshed(state: MessageState, action: PayloadAction<boolean>) {
      state.isRefreshed = action.payload;
    },
    updateChatRoomMessageLastSyncAt(
      state: MessageState,
      action: PayloadAction<{
        chatRoomId: string;
        uid: string;
        lastMessageSyncAt: string;
      }>,
    ) {
      parentChatRoomMessageSyncAdapter.upsertOne(state.chatRoomMessageSync, {
        id: action.payload.chatRoomId,
        uid: action.payload.uid,
        lastReadAt: null,
        lastSyncAt: action.payload.lastMessageSyncAt,
        latestReadAt: null,
        latestMessage: null,
        latestMessageCreatedAt: null,
      });
    },
    insertChatRoomMessage(
      state: MessageState,
      action: PayloadAction<{ uid: string; message: ChatRoomMessage }>,
    ) {
      const { uid, message } = action.payload;
      parentChatRoomMessageAdapter.addOne(state.chatRoomMessages, message);
      const latestMessageCreatedAt = message.user_uid === uid ? null : message.created_at;

      if (state.selectedChatRoomId === message.chat_room_id) {
        parentChatRoomMessageSyncAdapter.updateOne(state.chatRoomMessageSync, {
          id: message.chat_room_id,
          changes: {
            latestReadAt: message.created_at,
            latestMessage: message,
            latestMessageCreatedAt: latestMessageCreatedAt,
          },
        });
      } else {
        if (latestMessageCreatedAt === null) {
          parentChatRoomMessageSyncAdapter.updateOne(state.chatRoomMessageSync, {
            id: message.chat_room_id,
            changes: {
              latestMessage: message,
            },
          });
        } else {
          parentChatRoomMessageSyncAdapter.updateOne(state.chatRoomMessageSync, {
            id: message.chat_room_id,
            changes: {
              latestMessage: message,
              latestMessageCreatedAt: latestMessageCreatedAt,
            },
          });
        }
      }
    },
    bulkInsertChatRoomMessage(
      state: MessageState,
      action: PayloadAction<{ uid: string; messages: ChatRoomMessage[] }>,
    ) {
      const { uid, messages } = action.payload;

      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        const chatRoom = parentChatRoomAdapter
          .getSelectors()
          .selectById(state.chatRooms, lastMessage.chat_room_id);

        if (chatRoom) {
          parentChatRoomMessageSyncAdapter.upsertOne(state.chatRoomMessageSync, {
            id: lastMessage.chat_room_id,
            uid,
            lastSyncAt: lastMessage.created_at,
            latestReadAt: chatRoom.last_read_at,
            lastReadAt: chatRoom.last_read_at,
            latestMessage: messages[0],
            latestMessageCreatedAt: messages[0].user_uid === uid ? null : messages[0].created_at,
          });
        }
      }

      parentChatRoomMessageAdapter.upsertMany(state.chatRoomMessages, messages);
    },
    clearChatRoomMessages(state: MessageState) {
      parentChatRoomMessageAdapter.removeAll(state.chatRoomMessages);
    },
    // 新しいチャットルームを追加（既存データは保持）
    addParentChatRoom(state: MessageState, action: PayloadAction<ChatRoom>) {
      const room = action.payload;
      if (room.type === "talk") {
        parentChatRoomAdapter.addOne(state.chatRooms, room);
      }
    },
    // 複数のチャットルームを差分追加（既存データは保持）
    addParentChatRooms(state: MessageState, action: PayloadAction<ChatRoom[]>) {
      const rooms = action.payload.filter((room) => room.type === "talk");
      // 既存のIDは除外して新規のみ追加
      const existingIds = new Set(state.chatRooms.ids);
      const newRooms = rooms.filter((room) => !existingIds.has(room.id));
      if (newRooms.length > 0) {
        parentChatRoomAdapter.addMany(state.chatRooms, newRooms);
      }
    },
    // チャットルームを削除（メッセージも削除）
    removeParentChatRoom(state: MessageState, action: PayloadAction<string>) {
      const roomId = action.payload;
      parentChatRoomAdapter.removeOne(state.chatRooms, roomId);
      // 関連するメッセージと同期情報も削除
      const messagesToRemove = Object.values(state.chatRoomMessages.entities)
        .filter((msg) => msg?.chat_room_id === roomId)
        .map((msg) => msg!.id);
      parentChatRoomMessageAdapter.removeMany(state.chatRoomMessages, messagesToRemove);
      parentChatRoomMessageSyncAdapter.removeOne(state.chatRoomMessageSync, roomId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getParentChatRooms.fulfilled,
        (state: MessageState, action: PayloadAction<{ chat_rooms: ChatRoom[] }>) => {
          const { chat_rooms } = action.payload;
          // type = 'talk' のチャットルームのみフィルタリング
          const talkRooms = chat_rooms.filter((room) => room.type === "talk");
          parentChatRoomAdapter.removeAll(state.chatRooms);
          parentChatRoomMessageAdapter.removeAll(state.chatRoomMessages);
          parentChatRoomMessageSyncAdapter.removeAll(state.chatRoomMessageSync);
          if (talkRooms.length > 0) {
            parentChatRoomAdapter.addMany(state.chatRooms, talkRooms);
          }
        },
      )
      .addCase(
        markParentChatRoomMessage.fulfilled,
        (state: MessageState, action: PayloadAction<any>) => {
          const { chat_rooms_read_at } = action.payload;
          (
            chat_rooms_read_at as {
              chat_room_id: string;
              uid: string;
              last_read_at: string;
            }[]
          ).forEach((read_at) => {
            parentChatRoomMessageSyncAdapter.updateOne(state.chatRoomMessageSync, {
              id: read_at.chat_room_id,
              changes: {
                lastReadAt: read_at.last_read_at,
                latestReadAt: read_at.last_read_at,
              },
            });
          });
        },
      )
      .addCase(
        getParentChatRoomMessages.fulfilled,
        (state: MessageState, action: PayloadAction<any>) => {
          const { messages } = action.payload;
          if (messages.length > 0 && state.selectedChatRoomId) {
            parentChatRoomMessageSyncAdapter.updateOne(state.chatRoomMessageSync, {
              id: state.selectedChatRoomId,
              changes: {
                lastSyncAt: messages[messages.length - 1].created_at,
              },
            });

            parentChatRoomMessageAdapter.addMany(state.chatRoomMessages, messages);
          }
        },
      );
  },
});

export const messageActions = { ...messageSlice.actions };

export default messageSlice.reducer;
